import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import PropTypes from 'prop-types';

import { creators as FeesProjectionActions } from '../../../Reducers/Reporting/FeesProjection';
import { creators as ReportSettingActions } from '../../../Reducers/Reporting/ReportSetting';
import { financialMonth } from '../../../Utils/Functions';
import appdata from '../../../Services/appdata';
import Loading from '../../../Components/Loading';
import TopChart from './TopChart';
import MiddleChart from './MiddleChart';
import BottomChart from './BottomChart';

const FeeProjection = ({
  defaultYear,
  defaultMonth,
  defaultDimDate,
  defaultStartMonth,
  selectedCompany,
  feeProjectionSetting,
  bubbleFlag,
}) => {
  const dispatch = useDispatch();
  
  const feesProjectionSummary = useSelector(
    (state) => state?.Reporting?.Summary?.reportingSummaries?.feesProjection ?? '',
  );
  const summaryData = useSelector((state) => state?.Reporting?.feesprojection?.summaryData ?? []);
  const detailData = useSelector((state) => state?.Reporting?.feesprojection?.detailData ?? []);
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );
  
  const [resize, setResize] = useState(false);
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedTopItems, setSelectedTopItems] = useState([]);
  const [selectedMiddleItems, setSelectedMiddleItems] = useState([]);
  const [filterName, setFilterName] = useState('Director');
  const [middleHeight, setMiddleHeight] = useState(140);
  const [loading, setLoading] = useState(false);
  const lastFeeProjectionRequestKey = useRef(null);

  // Redux action creators
  const getFeesProjectionSummary = useCallback(
    (selectedYears, DateKey) =>
      dispatch(FeesProjectionActions.feesprojectionSummaryRequest(selectedYears, DateKey)),
    [dispatch],
  );

  const getFeesProjectionDetail = useCallback(
    (selectedYears, DateKey) =>
      dispatch(FeesProjectionActions.feesprojectionDetailRequest(selectedYears, DateKey)),
    [dispatch],
  );

  const reportSettingUpdateRequest = useCallback(
    (update_setting) => dispatch(ReportSettingActions.reportSettingUpdateRequest(update_setting)),
    [dispatch]
  );

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  const handleFilter = useCallback(async (event) => {
    if (event.selectedTopItems && event.selectedTopItems.length > 0) {
      let dimDateKeys = [];
      event.selectedTopItems.forEach((date) =>
        dimDateKeys.push(
          date.year.toString() + String(date.month + 1).padStart(2, '0') + '01',
        ),
      );
      setLoading(true);
      appdata.setLocalItem('fpSelectedTopItems', event.selectedTopItems);
      await getFeesProjectionDetail([defaultYear], dimDateKeys);
    }

    // Update state with event data
    if (event.selectedYears !== undefined) setSelectedYears(event.selectedYears);
    if (event.selectedMonths !== undefined) setSelectedMonths(event.selectedMonths);
    if (event.selectedTopItems !== undefined) setSelectedTopItems(event.selectedTopItems);
    if (event.selectedMiddleItems !== undefined) setSelectedMiddleItems(event.selectedMiddleItems);
    if (event.filterName !== undefined) setFilterName(event.filterName);

    if (event.selectedMiddleItems && event.selectedMiddleItems.length > 0)
      appdata.setLocalItem('fpSelectedMiddleItems', event.selectedMiddleItems);

    if (event.filterName)
      await reportSettingUpdateRequest({
        FeeProjection: JSON.stringify({
          filterName: event.filterName ? event.filterName : filterName,
        }),
      });
  }, [defaultYear, defaultMonth, defaultDimDate, selectedCompany, getFeesProjectionDetail, reportSettingUpdateRequest, filterName]);

  const getMiddleChartHeight = useCallback((height) => {
    setMiddleHeight(height);
  }, []);

  // Component mount effect
  useEffect(() => {
    window.addEventListener('resize', onResize);

    const initializeComponent = async () => {
      setSelectedYears([defaultYear]);
      setSelectedMonths([financialMonth(defaultMonth, defaultStartMonth) - 1]);
      
      const storedTopItems = appdata.getLocalItem('fpSelectedTopItems');
      const defaultTopItems = [{
        year: Math.floor(parseInt(defaultDimDate) / 10000),
        month: financialMonth(defaultMonth, defaultStartMonth) - 1,
      }];
      setSelectedTopItems(storedTopItems || defaultTopItems);

      const storedMiddleItems = appdata.getLocalItem('fpSelectedMiddleItems');
      setSelectedMiddleItems(storedMiddleItems || []);
    };

    initializeComponent();

    // Cleanup function
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const triggerFeeProjectionRequests = useCallback(() => {
    const requestKey = `${selectedCompany || ''}|${defaultYear}|${defaultDimDate}`;

    if (lastFeeProjectionRequestKey.current === requestKey) {
      return;
    }

    lastFeeProjectionRequestKey.current = requestKey;
    getFeesProjectionSummary([defaultYear], [defaultDimDate]);
    getFeesProjectionDetail([defaultYear], [defaultDimDate]);
  }, [selectedCompany, defaultYear, defaultDimDate, getFeesProjectionSummary, getFeesProjectionDetail]);

  // Component update effect for prop changes
  useEffect(() => {
    const updateComponent = async () => {
      const defaultMonthValue = financialMonth(defaultMonth, defaultStartMonth) - 1;
      const newTopItems = [{
        year: Math.floor(parseInt(defaultDimDate) / 10000),
        month: financialMonth(defaultMonth, defaultStartMonth) - 1,
      }];

      setSelectedYears([defaultYear]);
      setSelectedMonths([defaultMonthValue]);
      setSelectedTopItems(newTopItems);
      setSelectedMiddleItems([]);

      appdata.setLocalItem('fpSelectedTopItems', newTopItems);
      appdata.setLocalItem('fpSelectedMiddleItems', []);

      if (!feeProjectionSetting) {
        await reportSettingUpdateRequest({
          FeeProjection: JSON.stringify({
            filterName: '',
          }),
        });
      }

      triggerFeeProjectionRequests();
    };

    updateComponent();
  }, [
    defaultYear,
    defaultMonth,
    defaultStartMonth,
    defaultDimDate,
    selectedCompany,
    feeProjectionSetting,
    lastFetchedCompany,
    triggerFeeProjectionRequests,
    reportSettingUpdateRequest,
  ]);

  useEffect(() => {
    const run = async () => {
      const valuesChanged =
        lastFetchedCompany !== selectedCompany ||
        lastFetchedDimDate !== defaultDimDate;

      if (valuesChanged && lastFetchedCompany && lastFetchedDimDate) {
        triggerFeeProjectionRequests();
      }
    };

    run();
  }, [
    selectedCompany,
    defaultDimDate,
    defaultYear,
    lastFetchedCompany,
    lastFetchedDimDate,
    triggerFeeProjectionRequests,
  ]);

  // Effect to handle detailData changes
  useEffect(() => {
    setLoading(false);
  }, [detailData]);

  // Calculate project name lengths
  let projectNameLengths = [];
  let maxNameLength = 0;

  detailData.forEach((data) =>
    projectNameLengths.push(data.ProjectName.length),
  );
  if (projectNameLengths.length > 0)
    maxNameLength = Math.max(...projectNameLengths);

  return (
    <div style={{ height: '89vh' }}>
      {loading && <Loading />}

      <Box>
        <Box marginBottom={2}>
          <TopChart
            summaryData={summaryData}
            selectedYears={selectedYears}
            selectedMonths={selectedMonths}
            selectedTopItems={selectedTopItems}
            defaultStartMonth={defaultStartMonth}
            defaultMonth={defaultMonth}
            defaultYear={defaultYear}
            handleFilter={handleFilter}
          />
        </Box>
        <Box marginBottom={2}>
          <MiddleChart
            detailData={detailData}
            selectedYears={selectedYears}
            selectedMonths={selectedMonths}
            selectedTopItems={selectedTopItems}
            selectedMiddleItems={selectedMiddleItems}
            defaultStartMonth={defaultStartMonth}
            defaultMonth={defaultMonth}
            filterName={filterName}
            maxNameLength={maxNameLength}
            handleFilter={handleFilter}
            getMiddleChartHeight={getMiddleChartHeight}
          />
        </Box>
        <Box marginBottom={2}>
          <BottomChart
            detailData={detailData}
            selectedYears={selectedYears}
            selectedMonths={selectedMonths}
            selectedTopItems={selectedTopItems}
            selectedMiddleItems={selectedMiddleItems}
            filterName={filterName}
            middleHeight={middleHeight}
            maxNameLength={maxNameLength}
          />
        </Box>
      </Box>      

      {bubbleFlag && feesProjectionSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed',
            bottom: 60,
            right: 30,
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.5'}
        >
          <Tooltip 
            title={feesProjectionSummary} 
            position="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

FeeProjection.propTypes = {
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  selectedCompany: PropTypes.any,
  feeProjectionSetting: PropTypes.object.isRequired,
};

export default FeeProjection;
